'use client'

// Reports visits, page views and named clicks (lib/visits.js), and ties the browser to the account that signs in on
// it. Renders nothing; does nothing when NEXT_PUBLIC_VISIT_TRACKING is 'false'. Outside the auth gate in the root
// layout, so a logged-out visitor's arrival is counted before Firebase resolves.
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '../contexts/AuthContext'
import { hasTrackingConsent, onConsentChanged } from '../lib/consent'
import {
  VISIT_TRACKING,
  flush,
  forget,
  linkAccount,
  recordClick,
  recordPage,
} from '../lib/visits'

export default function VisitTracker() {
  const pathname = usePathname()
  const { user, authResolved } = useAuth()
  const [allowed, setAllowed] = useState(null)

  useEffect(() => {
    if (!VISIT_TRACKING) return undefined
    const update = () => setAllowed(hasTrackingConsent())
    update()
    return onConsentChanged(update)
  }, [])

  useEffect(() => {
    if (allowed === false) forget()
  }, [allowed])

  useEffect(() => {
    if (allowed !== null) recordPage(pathname, allowed)
  }, [pathname, allowed])

  useEffect(() => {
    if (allowed && authResolved && user) linkAccount(user, true)
  }, [allowed, authResolved, user])

  useEffect(() => {
    if (!VISIT_TRACKING) return undefined
    const onClick = (event) => recordClick(event.target, hasTrackingConsent())
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush(true)
    }
    const onLeave = () => flush(true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onLeave)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onLeave)
    }
  }, [])

  return null
}
